/**
 * Module-scoped "Add / Edit Rule" modal.
 *
 * Raw-JSON CRUD entry point for component rules on the admin module
 * edit page. The modal is deliberately hand-rolled (not an
 * `EntityForm`) because its `rule_json` field requires a Zod schema
 * that is not expressible through the `EntityForm` field types: the
 * admin types a JSON blob, the frontend parses it, and the parsed
 * value is validated against {@link ruleJsonSchema}. Any error is
 * rendered inline in an `Alert` and the Save button is disabled until
 * the JSON is valid.
 *
 * Component selector, name, description, nl-input, and priority are
 * plain base inputs (`Select`, `TextField`, native `<textarea>` where
 * no dedicated base component exists — the same precedent as
 * `WorkflowDefinitionDetailPage`'s JSON editor). The monospaced JSON
 * editor is a native `<textarea>` for the same reason.
 *
 * In edit mode:
 * - The `Component` select is disabled (component id is immutable).
 * - Every field is pre-populated from the `editingRule` prop.
 * - Save dispatches `useUpdateComponentRule()` instead of
 *   `useCreateComponentRule()`.
 *
 * On success the modal calls the parent's `onSaved` callback (which
 * invalidates the rules list query) and then `onClose()`.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@components/base/actions';
import { Text, Tooltip } from '@components/base/display';
import { Alert, Modal } from '@components/base/feedback';
import { RadioGroup, Select, TextField } from '@components/base/inputs';
import { HStack, VStack } from '@components/base/layout';
import {
  type ComponentRegistration,
  getComponent,
  ruleJsonSchema,
  validateEffectsAgainstRegistration,
} from '@components/shared/configurable';

import type {
  ComponentRuleDTO,
  ComponentRuleJsonDTO,
  CreateComponentRulePayload,
  UpdateComponentRulePayload,
} from '../../api/componentRulesService';
import { tenantsService } from '../../api/tenantsService';
import {
  useCreateComponentRule,
  useUpdateComponentRule,
  useUpdateComponentRuleVisibility,
} from '../../hooks/componentRules';
import { useAdminAuthStore } from '../../auth/store';
import { useIsAdminTenant } from '../../auth/useIsAdminTenant';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Starting value shown in the JSON editor for a brand-new rule.
 *
 * An empty `AND` group + empty effects list is the smallest payload the
 * backend will accept. Admins typically edit this into a real rule.
 */
const DEFAULT_RULE_JSON = JSON.stringify(
  {
    conditions: { operator: 'AND', conditions: [] },
    effects: [],
  } satisfies ComponentRuleJsonDTO,
  null,
  2,
);

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ModuleRuleFormModalProps {
  /** Whether the modal is currently open. */
  readonly open: boolean;
  /** Called when the modal should close (cancel or success). */
  readonly onClose: () => void;
  /**
   * Slug of the module being edited (matches
   * `ComponentRegistration.module`). Passed through from the parent
   * page — currently unused at runtime but kept in the API so future
   * module-aware API clients have it.
   */
  readonly moduleSlug: string;
  /**
   * Candidate components the new / edited rule can target. Must be
   * the result of `getComponentsByModule(moduleSlug)` so the selector
   * stays in sync with the table.
   */
  readonly availableComponents: ComponentRegistration<unknown>[];
  /**
   * When set, the modal opens in edit mode and pre-fills every field
   * from this rule. Leave undefined for create mode.
   */
  readonly editingRule?: ComponentRuleDTO;
  /**
   * Called after a successful save. The parent is responsible for
   * invalidating the rules list query; the modal only triggers it.
   */
  readonly onSaved: () => void;
}

// ---------------------------------------------------------------------------
// Helper: derive initial form state
// ---------------------------------------------------------------------------

interface FormState {
  componentId: string;
  name: string;
  description: string;
  nlInput: string;
  ruleJson: string;
  priority: string;
  // Editable in both create and edit mode. Non-admin tenants see a
  // disabled selector so they can observe the current scope without
  // changing it; the server independently enforces the admin gate.
  visibility: 'public' | 'private';
  // Target tenant for a Private rule when the author is on an admin
  // tenant. Hidden in edit mode (tenant is locked at creation) and for
  // non-admin authors. `null` falls through to the server's
  // session-based default.
  targetTenantId: string | null;
}

/**
 * Returns `true` when an Axios-like error looks like the server's
 * `VISIBILITY_FORBIDDEN` 403. Tolerant of three shapes so the detection
 * stays robust if origami tweaks the response: HTTP 403, the documented
 * error code, or a message mentioning "visibility".
 */
function looksLikeVisibilityForbidden(err: unknown): boolean {
  const errResponse = (
    err as { response?: { status?: number; data?: { error_code?: string } } }
  ).response;
  const errMessage = err instanceof Error ? err.message : '';
  return (
    errResponse?.status === 403 ||
    errResponse?.data?.error_code === 'VISIBILITY_FORBIDDEN' ||
    errMessage.toLowerCase().includes('visibility')
  );
}

/**
 * Build the initial form state for create or edit mode.
 *
 * In create mode the component id defaults to the first entry in
 * `availableComponents` so the select is never rendered blank; every
 * other field starts empty. In edit mode every value comes from the
 * row being edited, and the priority input re-stringifies to keep the
 * controlled-input contract.
 */
function buildInitialFormState(
  availableComponents: ComponentRegistration<unknown>[],
  editingRule: ComponentRuleDTO | undefined,
  activeTenantId: string | null,
): FormState {
  if (editingRule !== undefined) {
    return {
      componentId: editingRule.component_id,
      name: editingRule.name,
      description: editingRule.description ?? '',
      nlInput: editingRule.nl_input ?? '',
      ruleJson: JSON.stringify(editingRule.rule_json, null, 2),
      priority: String(editingRule.priority),
      visibility: editingRule.visibility ?? 'private',
      // Tenant is locked at creation — surface the rule's current
      // tenant id (may be null for legacy rows) but the create-mode UI
      // is the only consumer of this field, so the value is never read
      // back when `editingRule !== undefined`.
      targetTenantId: editingRule.tenant_id ?? null,
    };
  }
  return {
    componentId: availableComponents[0]?.id ?? '',
    name: '',
    description: '',
    nlInput: '',
    ruleJson: DEFAULT_RULE_JSON,
    priority: '0',
    visibility: 'private',
    targetTenantId: activeTenantId,
  };
}

// ---------------------------------------------------------------------------
// Helper: validate the JSON editor contents
// ---------------------------------------------------------------------------

interface JsonValidationResult {
  /** Parsed + schema-validated payload, when the editor is valid. */
  value: ComponentRuleJsonDTO | null;
  /** Human-readable error (multi-line), when the editor is invalid. */
  error: string | null;
}

/**
 * Permissive structural shape covering both Zod 3 (`unionErrors`) and
 * Zod 4 (`errors`) issue objects. Defined locally so {@link flattenIssues}
 * can recurse into union sub-errors without importing Zod's internal
 * types.
 */
interface ValidationIssueLike {
  readonly code?: string;
  readonly path: ReadonlyArray<PropertyKey>;
  readonly message: string;
  readonly errors?: ReadonlyArray<{ readonly issues: ReadonlyArray<ValidationIssueLike> }>;
  readonly unionErrors?: ReadonlyArray<{ readonly issues: ReadonlyArray<ValidationIssueLike> }>;
}

interface IssueDescription {
  readonly path: string;
  readonly message: string;
}

/**
 * Flatten Zod's union issues so the user sees the closest-matching
 * branch's specific errors (e.g.,
 * `conditions.conditions.0.field: field is required`) instead of a
 * generic `Invalid input` placeholder. Picks the union branch with the
 * fewest top-level issues — that branch is the one the input was closest
 * to satisfying.
 */
function flattenIssues(
  issues: ReadonlyArray<ValidationIssueLike>,
): IssueDescription[] {
  const out: IssueDescription[] = [];
  for (const issue of issues) {
    // Use explicit `!== undefined` narrowing rather than `Array.isArray()`,
    // which widens the inferred element type to `any` and breaks downstream
    // typing for the reduce callback.
    const branches = issue.errors ?? issue.unionErrors;
    if (
      issue.code === 'invalid_union' &&
      branches !== undefined &&
      branches.length > 0
    ) {
      const closest = branches.reduce((a, b) =>
        a.issues.length <= b.issues.length ? a : b,
      );
      out.push(...flattenIssues(closest.issues));
      continue;
    }
    out.push({
      path: issue.path.map((p) => String(p)).join('.'),
      message: issue.message,
    });
  }
  return out;
}

/**
 * Parse the JSON editor value and validate it against
 * {@link ruleJsonSchema}. Returns a tagged result object so the caller
 * can render the error and enable / disable the Save button without
 * repeating the parse.
 */
function validateRuleJson(source: string): JsonValidationResult {
  const trimmed = source.trim();
  if (trimmed === '') {
    return { value: null, error: 'Rule JSON is required.' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return {
      value: null,
      error:
        err instanceof Error
          ? `Invalid JSON: ${err.message}`
          : 'Invalid JSON.',
    };
  }
  const result = ruleJsonSchema.safeParse(parsed);
  if (!result.success) {
    // Cast: Zod's `$ZodIssue` is a discriminated union with branch-specific
    // properties; `ValidationIssueLike` is a permissive structural shape
    // covering only the fields `flattenIssues` reads.
    const flat = flattenIssues(
      result.error.issues as unknown as readonly ValidationIssueLike[],
    );
    if (flat.length === 0) {
      return { value: null, error: 'Rule JSON failed validation.' };
    }
    const lines = flat
      .slice(0, 3)
      .map(({ path, message }) =>
        path === '' ? message : `${path}: ${message}`,
      );
    return {
      value: null,
      error: lines.join('\n'),
    };
  }
  return { value: result.data, error: null };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Raw-JSON rule form modal.
 *
 * Renders a `Modal` with form fields and a Save action. State is held
 * locally — the modal resets itself when the `editingRule` prop
 * changes, via the derived-state reset pattern below, so reopening it
 * for a different row always shows the correct values.
 */
export function ModuleRuleFormModal({
  open,
  onClose,
  moduleSlug: _moduleSlug,
  availableComponents,
  editingRule,
  onSaved,
}: Readonly<ModuleRuleFormModalProps>) {
  const isEdit = editingRule !== undefined;
  const activeTenantId = useAdminAuthStore((s) => s.activeTenantId);

  // Derived-state reset pattern: we track the last-seen `open` and
  // `editingRule` values, and when either changes we synchronously
  // recompute the form state during render. This is React's official
  // alternative to `useEffect(setState)` for resetting local state from
  // prop changes — see https://react.dev/reference/react/useState#storing-information-from-previous-renders.
  const [prevOpen, setPrevOpen] = useState(open);
  const [prevEditingRule, setPrevEditingRule] = useState(editingRule);
  const [state, setState] = useState<FormState>(() =>
    buildInitialFormState(availableComponents, editingRule, activeTenantId),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (open !== prevOpen || editingRule !== prevEditingRule) {
    setPrevOpen(open);
    setPrevEditingRule(editingRule);
    if (open) {
      setState(
        buildInitialFormState(availableComponents, editingRule, activeTenantId),
      );
      setSubmitError(null);
    }
  }

  // ---- JSON validation --------------------------------------------------

  const jsonValidation = useMemo(
    () => validateRuleJson(state.ruleJson),
    [state.ruleJson],
  );

  // ---- Registration-aware effect guardrail ------------------------------

  // Runs only after the structural parse succeeds: rejects any
  // `filterPropValues` effect whose prop the selected component does NOT pass
  // as a filterable data array (the silent-no-op trap — see
  // `validateEffectsAgainstRegistration`, which suggests `setProp`). Resolved
  // against the live registry; skips gracefully when the component is unknown.
  const effectGuardError = useMemo(() => {
    if (jsonValidation.value === null) return null;
    const registration = getComponent(state.componentId);
    if (registration === undefined) return null;
    const errors = validateEffectsAgainstRegistration(
      jsonValidation.value.effects,
      registration,
    );
    return errors.length === 0
      ? null
      : errors.map((e) => e.message).join('\n');
  }, [jsonValidation.value, state.componentId]);

  // ---- Basic field-level validation -------------------------------------

  const trimmedName = state.name.trim();
  const nameError =
    trimmedName === ''
      ? 'Name is required.'
      : trimmedName.length > 200
        ? 'Name must be 200 characters or fewer.'
        : null;

  const descriptionError =
    state.description.length > 2000
      ? 'Description must be 2000 characters or fewer.'
      : null;

  const nlInputError =
    state.nlInput.length > 1000
      ? 'Natural-language description must be 1000 characters or fewer.'
      : null;

  const parsedPriority = Number(state.priority);
  const priorityError =
    state.priority.trim() === '' || !Number.isFinite(parsedPriority)
      ? 'Priority must be an integer.'
      : null;

  const componentIdError =
    state.componentId === '' ? 'Component is required.' : null;

  const isFormValid =
    jsonValidation.value !== null &&
    effectGuardError === null &&
    nameError === null &&
    descriptionError === null &&
    nlInputError === null &&
    priorityError === null &&
    componentIdError === null;

  // Admin-tenant flag — gates the Public visibility option.
  const isAdminTenant = useIsAdminTenant();

  // ---- Tenant list (create-mode Private target selector) ---------------

  // Enabled in both create and edit mode so the read-only "Scoped to
  // tenant" caption (edit) and the target selector (create) can both
  // resolve tenant ids to display names from the same cached payload.
  const tenantsQuery = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => tenantsService.list(),
    staleTime: 5 * 60 * 1000,
    enabled: isAdminTenant,
  });

  const tenantOptions = useMemo(
    () =>
      (tenantsQuery.data?.data ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: String(t.id), label: t.name })),
    [tenantsQuery.data],
  );

  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tenantsQuery.data?.data ?? []) {
      map.set(String(t.id), t.name);
    }
    return (id: string): string | undefined => map.get(id);
  }, [tenantsQuery.data]);

  // ---- Mutations --------------------------------------------------------

  const createMutation = useCreateComponentRule();
  const updateMutation = useUpdateComponentRule();
  const updateVisibilityMutation = useUpdateComponentRuleVisibility();

  const isSaving =
    createMutation.isPending ||
    updateMutation.isPending ||
    updateVisibilityMutation.isPending;

  // ---- Select options ---------------------------------------------------

  const componentOptions = useMemo(
    () =>
      availableComponents.map((reg) => ({
        value: reg.id,
        label: reg.displayName,
      })),
    [availableComponents],
  );

  // ---- Handlers ---------------------------------------------------------

  const handleCancel = () => {
    if (isSaving) return;
    onClose();
  };

  const handleSave = () => {
    if (!isFormValid || jsonValidation.value === null) return;
    setSubmitError(null);

    const description = state.description.trim();
    const nlInput = state.nlInput.trim();
    const priority = Math.trunc(parsedPriority);

    if (editingRule !== undefined) {
      // The general PUT endpoint never accepts `visibility` — the
      // server routes that transition through a dedicated PATCH. Keep
      // the update payload narrow (content-only fields) and, after the
      // PUT resolves, fire the visibility mutation IF the admin
      // actually changed it. We diff client-side so the backend's
      // 422-on-same-to-same response never fires (the PATCH rejects a
      // no-op change).
      const submittedVisibility = state.visibility;
      const visibilityChanged =
        submittedVisibility !== editingRule.visibility;
      const payload: UpdateComponentRulePayload = {
        name: trimmedName,
        description: description === '' ? null : description,
        nl_input: nlInput === '' ? null : nlInput,
        rule_json: jsonValidation.value,
        priority,
      };
      updateMutation.mutate(
        {
          id: editingRule.id,
          payload,
          componentId: editingRule.component_id,
        },
        {
          onSuccess: () => {
            // If visibility is unchanged we're done — close the modal
            // and let the parent invalidate the list. If it did change,
            // fire the dedicated PATCH sequentially; the modal stays
            // open until that resolves so a visibility failure can
            // surface inline without rolling back the PUT.
            if (!visibilityChanged) {
              onSaved();
              onClose();
              return;
            }
            updateVisibilityMutation.mutate(
              {
                id: editingRule.id,
                visibility: submittedVisibility,
                componentId: editingRule.component_id,
              },
              {
                onSuccess: () => {
                  onSaved();
                  onClose();
                },
                onError: (err) => {
                  if (looksLikeVisibilityForbidden(err)) {
                    setSubmitError(
                      'Public rules require an admin tenant.',
                    );
                    return;
                  }
                  setSubmitError(
                    err instanceof Error
                      ? err.message
                      : 'Failed to update visibility.',
                  );
                },
              },
            );
          },
          onError: (err) => {
            setSubmitError(
              err instanceof Error
                ? err.message
                : 'The server rejected the rule payload.',
            );
          },
        },
      );
      return;
    }

    // Only send `visibility` when the admin opted into Public; the
    // server defaults the omitted field to `'private'`.
    const submittedVisibility = state.visibility;
    const createPayload: CreateComponentRulePayload = {
      component_id: state.componentId,
      name: trimmedName,
      description: description === '' ? null : description,
      nl_input: nlInput === '' ? null : nlInput,
      rule_json: jsonValidation.value,
      priority,
      ...(submittedVisibility === 'public'
        ? { visibility: 'public' as const }
        : {}),
      // Mirror ComponentConfigPage: only send `tenant_id` when the
      // author is an admin tenant, kept the rule Private, the tenant
      // list query loaded successfully, and the selector resolved to a
      // non-null value. The query-failure path falls back to letting
      // the server infer tenancy from the session.
      ...(isAdminTenant &&
      submittedVisibility === 'private' &&
      !tenantsQuery.isError &&
      state.targetTenantId !== null
        ? { tenant_id: state.targetTenantId }
        : {}),
    };
    createMutation.mutate(createPayload, {
      onSuccess: () => {
        onSaved();
        onClose();
      },
      onError: (err) => {
        if (
          submittedVisibility === 'public' &&
          looksLikeVisibilityForbidden(err)
        ) {
          setSubmitError('Public rules require an admin tenant.');
          return;
        }
        setSubmitError(
          err instanceof Error
            ? err.message
            : 'The server rejected the rule payload.',
        );
      },
    });
  };

  // ---- Render -----------------------------------------------------------

  return (
    <Modal open={open} onClose={handleCancel} size="lg">
      <Modal.Header title={isEdit ? 'Edit rule' : 'Add rule'} />
      <Modal.Body>
        <VStack gap={4}>
          <Alert variant="info" title="Raw-JSON path">
            Conditions and effects go directly to the backend. For the
            NL-driven flow, use the per-component config page instead.
          </Alert>

          <Select
            label="Component"
            required
            options={componentOptions}
            value={state.componentId}
            onChange={(value) => {
              if (typeof value === 'string') {
                setState((prev) => ({ ...prev, componentId: value }));
              }
            }}
            disabled={isEdit}
            error={componentIdError !== null}
            errorMessage={componentIdError ?? undefined}
            placeholder="Select a component"
          />

          <TextField
            label="Name"
            required
            value={state.name}
            onChange={(e) => {
              setState((prev) => ({ ...prev, name: e.target.value }));
            }}
            placeholder="e.g. Hide coverage card for Sinistre"
            error={nameError !== null}
            errorMessage={nameError ?? undefined}
            maxLength={200}
          />

          <VStack gap={1}>
            <Text
              as="label"
              htmlFor="module-rule-description"
              size="sm"
              weight="medium"
            >
              Description
            </Text>
            <textarea
              id="module-rule-description"
              value={state.description}
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  description: e.target.value,
                }));
              }}
              rows={2}
              maxLength={2000}
              className="w-full rounded-md border border-secondary-300 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
              placeholder="Optional notes about why this rule exists"
            />
            {descriptionError !== null && (
              <Text size="xs" color="error">
                {descriptionError}
              </Text>
            )}
          </VStack>

          <VStack gap={1}>
            <Text
              as="label"
              htmlFor="module-rule-nl-input"
              size="sm"
              weight="medium"
            >
              Natural language description (for record keeping only —
              not translated)
            </Text>
            <textarea
              id="module-rule-nl-input"
              value={state.nlInput}
              onChange={(e) => {
                setState((prev) => ({ ...prev, nlInput: e.target.value }));
              }}
              rows={3}
              maxLength={1000}
              className="w-full rounded-md border border-secondary-300 bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
              placeholder="e.g. hide if task_type is not Declaration or Constat"
            />
            {nlInputError !== null && (
              <Text size="xs" color="error">
                {nlInputError}
              </Text>
            )}
          </VStack>

          <VStack gap={1}>
            <Text
              as="label"
              htmlFor="module-rule-json"
              size="sm"
              weight="medium"
            >
              Rule JSON
              <span className="ml-0.5 text-error-700" aria-hidden="true">
                *
              </span>
            </Text>
            <textarea
              id="module-rule-json"
              value={state.ruleJson}
              onChange={(e) => {
                setState((prev) => ({ ...prev, ruleJson: e.target.value }));
              }}
              rows={14}
              spellCheck={false}
              className="w-full rounded-md border border-secondary-300 bg-white p-3 font-mono text-xs leading-5 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
              aria-invalid={jsonValidation.error !== null}
            />
            {jsonValidation.error !== null && (
              <Alert variant="error" title="Invalid rule JSON">
                <span className="whitespace-pre-line">
                  {jsonValidation.error}
                </span>
              </Alert>
            )}
            {jsonValidation.error === null && effectGuardError !== null && (
              <Alert variant="error" title="Effect not allowed">
                <span className="whitespace-pre-line">{effectGuardError}</span>
              </Alert>
            )}
          </VStack>

          <VStack gap={1}>
            {(() => {
              // Edit-mode non-admins see the current scope but cannot
              // change it — disable BOTH radios so nothing is editable.
              // Create-mode non-admins can still pick Private (default),
              // only Public is locked.
              const disableWholeGroup = isEdit && !isAdminTenant;
              const group = (
                <RadioGroup
                  name="module-rule-visibility"
                  label="Visibility"
                  orientation="horizontal"
                  value={state.visibility}
                  onChange={(next) => {
                    if (next === 'public' || next === 'private') {
                      setState((prev) => ({
                        ...prev,
                        visibility: next,
                        // Flipping to Public clears the tenant (public
                        // rules are tenant-less server-side). Flipping
                        // back to Private re-seeds from the active
                        // tenant if the admin hadn't picked one yet.
                        targetTenantId:
                          next === 'public'
                            ? null
                            : prev.targetTenantId ?? activeTenantId,
                      }));
                    }
                  }}
                  options={[
                    {
                      value: 'private',
                      label: 'Private',
                      disabled: disableWholeGroup,
                    },
                    {
                      value: 'public',
                      label: 'Public',
                      disabled: disableWholeGroup || !isAdminTenant,
                    },
                  ]}
                />
              );
              if (disableWholeGroup) {
                return (
                  <Tooltip content="Only admin tenants can change visibility.">
                    {group}
                  </Tooltip>
                );
              }
              if (!isAdminTenant) {
                return (
                  <Tooltip content="Only admin tenants can create public rules.">
                    {group}
                  </Tooltip>
                );
              }
              return group;
            })()}
            {!isEdit && (
              <Text size="sm" color="muted">
                Public rules are shared across all tenants. Requires admin.
              </Text>
            )}
          </VStack>

          {isAdminTenant && !isEdit && state.visibility === 'private' && (
            <VStack gap={1}>
              <Select
                label="Target tenant"
                required
                searchable
                options={tenantOptions}
                value={state.targetTenantId ?? ''}
                onChange={(value) => {
                  if (typeof value === 'string') {
                    setState((prev) => ({ ...prev, targetTenantId: value }));
                  }
                }}
                disabled={tenantsQuery.isLoading}
                placeholder={
                  tenantsQuery.isLoading
                    ? 'Loading tenants...'
                    : 'Select a tenant'
                }
                error={tenantsQuery.isError}
                errorMessage={
                  tenantsQuery.isError ? 'Failed to load tenants.' : undefined
                }
              />
              {tenantsQuery.isError ? (
                <Alert variant="warning" title="Tenant list unavailable">
                  Tenant list unavailable — private rules will be scoped to
                  your own tenant.
                </Alert>
              ) : (
                <Text size="sm" color="muted">
                  Private rules are scoped to the chosen tenant only.
                </Text>
              )}
            </VStack>
          )}
          {isAdminTenant &&
            isEdit &&
            editingRule !== undefined &&
            editingRule.tenant_id !== null && (
              <Text size="sm" color="muted">
                Scoped to tenant:{' '}
                {tenantNameById(editingRule.tenant_id) ?? editingRule.tenant_id}
              </Text>
            )}

          <TextField
            label="Priority"
            type="number"
            value={state.priority}
            onChange={(e) => {
              setState((prev) => ({ ...prev, priority: e.target.value }));
            }}
            error={priorityError !== null}
            errorMessage={priorityError ?? undefined}
          />

          {submitError !== null && (
            <Alert variant="error" title="Save failed">
              {submitError}
            </Alert>
          )}
        </VStack>
      </Modal.Body>
      <Modal.Footer>
        <HStack gap={2} justify="end">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={!isFormValid}
          >
            Save
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  );
}
